import { useState, useEffect } from "react";
import { getInvoiceReport } from "../services/analyticsService";

/**
 * Fetches invoice-based report for a specific month (totals and provider from monthly_invoices by invoice_date).
 * @param {string|number} tenantId
 * @param {number|null} year
 * @param {number|null} month
 * @returns {Object} { data, loading, error, refresh }
 */
export const useInvoiceReport = (tenantId, year, month) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenantId || year == null || month == null) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const response = await getInvoiceReport(tenantId, year, month);
        if (cancelled) return;
        if (response?.success && response?.data != null) {
          setData(response.data);
        } else {
          setData(null);
          setError(new Error(response?.message || "No invoice data for this month"));
        }
      } catch (err) {
        if (cancelled) return;
        setError(err);
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, year, month]);

  const refresh = async () => {
    if (!tenantId || year == null || month == null) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getInvoiceReport(tenantId, year, month);
      if (response?.success && response?.data != null) {
        setData(response.data);
      } else {
        setData(null);
        setError(new Error(response?.message || "No invoice data for this month"));
      }
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refresh };
};
